import React, {FC, Fragment, ReactNode} from 'react';
import styled from 'styled-components';

import {
  KeyboardActionsEnum,
  KeyboardMapping,
} from '../../../../libs/shared/core/src/helpers/browser/keyboardHelpers';
import {intersperse} from '../../../../libs/shared/core/src/helpers/utilities/arrayHelpers';
import {KeyboardContext} from '../../../../libs/shared/ui/src/components/keyboard/keyboardContext';
import {
  keyRenderers,
  splitAndFixKeys,
} from '../../../../libs/shared/ui/src/components/keyboard/keyboardShortcutFormatterHelpers';
import {fonts, fontSizes} from '../../../../libs/shared/ui/src/styles/fontStyles';

/*
 * Props.
 */

interface CommandPaletteKeyboardFormatterProps {
  action: KeyboardActionsEnum;
}

/*
 * Style.
 */

const StyledKeySpan = styled.span`
  font-family: ${fonts.monospace};
  font-size: ${fontSizes.tiny};
  font-style: normal;
  padding: 3px 4px;
  border-radius: 4px;
  box-shadow:
    0 0 0 1px ${(p) => p.theme.greys.shade50},
    inset 0 -1px 0 ${(p) => p.theme.greys.shade40};
  background: ${(p) => p.theme.greys.shade10};
  color: ${(p) => p.theme.greys.shade80};
  display: inline-block;
  text-transform: uppercase;
  margin: 0px 2px;
`;

/*
 * Component.
 */

export const CommandPaletteKeyboardFormatter: FC<CommandPaletteKeyboardFormatterProps> = (props) => (
  <KeyboardContext.Consumer>
    {(context) => renderKeyboardShortcuts(context.mapping, props.action)}
  </KeyboardContext.Consumer>
);

function renderKeyboardShortcuts(mapping: KeyboardMapping, action: KeyboardActionsEnum) {
  const shortcut = mapping[action][0];

  if (!shortcut) {
    return null;
  }

  return shortcut.sequence.split(' ').map(renderKeyboardShortcutKeys);
}

/** Render a group of keys. */
function renderKeyboardShortcutKeys(shortcut: string) {
  const fixedKeys = splitAndFixKeys(shortcut);

  // Find the correct translation for each.
  const translatedKeys = fixedKeys.map((k) => {
    const renderer = keyRenderers[k];
    return <StyledKeySpan key={k}>{renderer ? renderer() : k}</StyledKeySpan>;
  });

  // Render, separated by "+" signs.
  return <Fragment key={shortcut}>{intersperse<ReactNode>(translatedKeys, ' ')}</Fragment>;
}
